import { useCallback } from 'react';
import useFilePaste, { isPastedImage } from '../common/useFilePaste';

const EXT_BY_TYPE = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/webp': 'webp',
    'image/gif': 'gif',
    'image/bmp': 'bmp',
    'image/avif': 'avif',
    'image/tiff': 'tiff',
};

/**
 * Clipboard images always arrive as "image.png", so two different screenshots
 * pasted in a row look identical to the name+size de-duplication every image
 * tool does. Stamping the name keeps them apart.
 *
 * A PDF copied in the file manager keeps its own name, which is why this is the
 * image hook's business and not the shared one's.
 */
function nameClipboardFile(file, index) {
    const ext = EXT_BY_TYPE[file.type] || 'png';
    const stamp = `${Date.now()}${index ? `-${index + 1}` : ''}`;
    return new File([file], `pasted-${stamp}.${ext}`, {
        type: file.type,
        lastModified: Date.now(),
    });
}

/**
 * Ctrl/Cmd+V anywhere on a tool page drops the copied picture straight into it.
 *
 * @param {(files: File[]) => void} onFiles receives the pasted image files.
 * @param {{ enabled?: boolean }} [options] pass `enabled: false` to ignore
 *   pastes while a run is in flight.
 */
export default function useImagePaste(onFiles, { enabled = true } = {}) {
    return useFilePaste(onFiles, {
        enabled,
        accept: isPastedImage,
        rename: useCallback(nameClipboardFile, []),
    });
}
